import { ContextStore } from 'bernie-plugin-mobx';
import { inject, observer } from 'mobx-react';
import { RouteComponentProps, useHistory, useLocation } from 'react-router-dom';
import * as React from 'react';

import { ErrorBoundary } from 'bernie-view';
import { SerializedData, updateSearch } from 'bernie-core';

import { ILocalization, LocalizedComponentProps, useLocalization } from '@shared-ui/localization-context';

import { CurrentHotelStore } from 'stores/current-hotel-store';
import { InfositeQueryAdapter } from 'stores/adapters/infosite-query-adapter';
import { SearchCriteriaModel, singleUseQueryParams } from 'stores/models';
import { SearchQueryAdapter } from 'stores/adapters/search-query-adapter';

import { UitkSpacing } from 'uitk-react-spacing';

import { multiItemCompleteSearchCriteria } from 'components/multi-item/utils';

// @ts-ignore
import { History, Location } from 'history';
import { parse } from 'query-string';
import { SearchWizardComposer, SearchWizardComposerProps } from './search-wizard-composer';

export interface SingleOfferSearchFormProps extends RouteComponentProps {
  context?: ContextStore;
  currentHotel?: CurrentHotelStore;
}
type LocalizedSingleOfferSearchFormProps = LocalizedComponentProps & SingleOfferSearchFormProps;

export const SingleOfferSearchForm: React.FunctionComponent<any> = props => <WrappedSingleOffer {...props} />;

SingleOfferSearchForm.displayName = 'SingleOfferSearchForm';

export const WrappedSingleOffer: React.FC<LocalizedSingleOfferSearchFormProps> = inject(
  'currentHotel',
  'context'
)(
  observer(({ currentHotel, context }) => {
    const l10n = useLocalization();
    const history = useHistory();
    const location = useLocation();

    const navigateTo = (submitUrl: any) => {
      history.push(submitUrl);
    };

    const onUpdateResults = (searchCriteria: SearchCriteriaModel) => {
      handleUpdateResults({
        context,
        criteria: searchCriteria.toJSON(multiItemCompleteSearchCriteria),
        history,
        location,
        l10n,
      });
    };
    React.useEffect(() => {
      const searchCriteria = currentHotel || undefined;

      const { startDate, endDate, adults, children } = SearchQueryAdapter.parseOut(searchCriteria);
      // 0 - children from search is undefined
      const childrenFormatted = typeof children === 'undefined' ? '' : children;

      currentHotel?.setOfferData({ startDate, endDate, adults, children: childrenFormatted });
    }, [currentHotel]);

    const searchWizardComposerProps: SearchWizardComposerProps = {
      navigateTo,
      onUpdateResults,
    };

    return (
      <ErrorBoundary label="hotel.information.searchform.singleoffer">
        <UitkSpacing padding={{ inline: 'three' }}>
          <div>
            <SearchWizardComposer {...searchWizardComposerProps} />
          </div>
        </UitkSpacing>
      </ErrorBoundary>
    );
  })
);

interface HandleUpdateResultsInput {
  context?: ContextStore;
  criteria: SerializedData;
  history: History;
  location: Location;
  l10n: ILocalization;
}

export const handleUpdateResults = ({ context, criteria, history, location, l10n }: HandleUpdateResultsInput) => {
  const params = { ...criteria, localDateFormat: context?.localDateFormat };
  const paramsFilter = (key: string) => !key.startsWith('pwaDialog') && !key.startsWith('rm') && !singleUseQueryParams.includes(key);
  const newParams = InfositeQueryAdapter.parseOut(params, l10n);

  const hasOpenDialog = Boolean(parse(location.search).pwaDialog);
  if (hasOpenDialog) {
    // HACK: we used to listen for window popstate event before making the call to updateSearch to update url params
    // but bernie no longer calls history.goBack() when a dialog closes so the event is never fired. Until we can come
    // up with another bernie fix for the race condition that occurs between this updateSearch call and the same call in
    // bernie dialog-context - we'll have to use the setTimeout here
    setTimeout(() => updateSearch({ history, location, newParams, replace: true, paramsFilter }), 50);
  } else {
    updateSearch({ history, location, newParams, replace: true, paramsFilter });
  }
};
